import { SyncEnvironment } from '../components/AddEditOffer/SyncSection';
import { Offer } from '../types';
import { env } from '@core/config/env';

// Export all utility functions and constants
export * from './formatters';
export * from './helpers';
export * from './labelHelpers';

/**
 * Format premium IDs from form data to API format
 * Converts array of {premiumId, amount} to "optionId=amount,optionId=amount" string
 */
export const formatPremiumIds = (premiumEntries: Array<{ premiumId: number; amount: number }>): string[] => {
  return premiumEntries.map((entry) => `${entry.premiumId}=${entry.amount}`);
};

/**
 * Parse premium IDs from API format to form data
 * Converts "optionId=amount,optionId=amount" string to array of {premiumId, amount}
 */
export const parsePremiumIds = (premiumIds: string[]): Array<{ premiumId: number; amount: number }> => {
  if (!premiumIds) return [];
  return premiumIds.map((id) => {
    const [premiumId, amount] = id.split('=');
    return {
      premiumId: parseInt(premiumId),
      amount: parseInt(amount) || 0,
    };
  });
};

// Date utilities are now exported from formatters.ts which re-exports from @core/utils/dateUtils

/**
 * Validate file type for image uploads
 */
export const isValidImageType = (file: File): boolean => {
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  return validTypes.includes(file.type);
};

/**
 * Validate file size (max 5MB)
 */
export const isValidFileSize = (file: File, maxSizeMB: number = 5): boolean => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxSizeBytes;
};

/**
 * Generate a unique filename for uploads
 */
export const generateUniqueFileName = (originalName: string): string => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 8);
  const extension = originalName.split('.').pop();
  return `${timestamp}_${random}.${extension}`;
};

// Converts API response customFields to form default object
export function buildCustomFieldDefaults(apiCustomFields: any[], dynamicFields: any[]) {
  const defaults: Record<string, any> = {};
  apiCustomFields.forEach((f) => {
    const field = dynamicFields.find((df) => df.fieldId === f.fieldId);
    if (!field) return;
    if (field.type === 'select') {
      defaults[field.key] = f.values[0]?.value || '';
    }
    if (field.type === 'multiselect') {
      defaults[field.key] = Array.isArray(f.values) ? f.values.map((v) => v.value) : [];
    } else {
      defaults[field.key] = f.values?.[0]?.value ?? '';
    }
  });
  return defaults;
}

// Converts form values to customFields array for API
export function buildCustomFieldsPayload(formValues: any, dynamicFields: any[]) {
  return dynamicFields
    .map((f) => {
      const value = formValues[f.key];

      // Skip if value is undefined, null, empty string, or empty array
      if (
        value === undefined ||
        value === null ||
        (typeof value === 'string' && value.trim() === '') ||
        (Array.isArray(value) && value.length === 0)
      ) {
        return null;
      }

      if (f.type === 'select') {
        const selectedOption = f.options?.find((opt) => opt.value === value);
        if (!selectedOption) return null;
        return {
          fieldId: f.fieldId,
          optionIds: [selectedOption.optionId],
          value: '',
        };
      }

      if (f.type === 'multiselect') {
        const selectedOptions =
          Array.isArray(value) && f.options
            ? f.options.filter((opt) => value.includes(opt.value)).map((opt) => opt.optionId)
            : [];
        if (selectedOptions.length === 0) return null;
        return {
          fieldId: f.fieldId,
          optionIds: selectedOptions,
          value: '',
        };
      }

      return {
        fieldId: f.fieldId,
        value,
      };
    })
    .filter(Boolean); // remove nulls
}

export const getEnvironment = (sync_on) => {
  if (sync_on === SyncEnvironment.PRODUCTION) return SyncEnvironment.PRODUCTION;
  if (sync_on === 'staging') return SyncEnvironment.SANDBOX;
  return SyncEnvironment.DRAFT;
};

export const getSyncOnValue = (environment: string) => {
  if (environment === SyncEnvironment.PRODUCTION) return SyncEnvironment.PRODUCTION;
  if (environment === SyncEnvironment.SANDBOX) return 'staging';
  return SyncEnvironment.DRAFT;
};

export const getOfferId = (offer: Offer) => {
  if (offer?.external_offer_id) {
    return `Sandbox Id #${offer?.external_offer_id}`;
  } else if (offer?.prod_external_offer_id) {
    return `Production Id #${offer?.prod_external_offer_id}`;
  } else {
    return `Offer ID: ${offer?.offer_id}`;
  }
};

export const resolveImageUrl = (value?: string) => {
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  return `${env.imageUrl}/${value}`;
};
